import type { ComponentType, SVGProps } from "react"
import { IconAlarmClockFill18 } from "nucleo-ui-fill-18"
import {
	IconAlarmClockOutline18,
	IconArrowDottedRotateAnticlockwiseOutline18,
	IconArrowRotateAnticlockwiseOutline18,
	IconArrowsLeftRightTrailOutline18,
	IconAtom2Outline18,
	IconAtSignOutline18,
	IconBellOutline18,
	IconBookOpen4Outline18,
	IconBracketsCurlyOutline18,
	IconCheckDoubleOutline18,
	IconCircleCheck3Outline18,
	IconCircleCheckOutline18,
	IconCircleDashedOutline18,
	IconClockSnoozeOutline18,
	IconCreditCardOutline18,
	IconFileDownloadOutline18,
	IconGaugeOutline18,
	IconInboxArrowDownOutline18,
	IconMessageHeartOutline18,
	IconMessagesOutline18,
	IconTagOutline18,
} from "nucleo-ui-outline-18"

import { cn } from "@productlane/lib/src/cn"
import { SlackIconGray } from "@productlane/ui"

type IconComponent = ComponentType<SVGProps<SVGSVGElement>>

// Nucleo tags the notification glyph's corner dot as color-2; the frame stays the text color.
const MENTION_DOT_CLASS =
	"[&_[data-color=color-2]]:fill-red-500 [&_[data-color=color-2]]:text-red-500"

/** Paints the nav glyph's corner dot red while any mention is unread. */
export function mentionDotClass(unreadMentions: number) {
	return unreadMentions > 0 ? MENTION_DOT_CLASS : undefined
}

function SnoozeEndedIcon({ className, ...props }: SVGProps<SVGSVGElement>) {
	return (
		<IconAlarmClockFill18
			{...props}
			className={cn(className, "text-violet-500 dark:text-violet-400")}
		/>
	)
}

const ICONS: Record<string, IconComponent> = {
	"thread.created": IconInboxArrowDownOutline18,
	"thread.reply_received": IconMessagesOutline18,
	"thread.reaction_received": IconMessageHeartOutline18,
	"thread.assigned": IconInboxArrowDownOutline18,
	"thread.unassigned": IconInboxArrowDownOutline18,
	"thread.done": IconCircleCheckOutline18,
	"thread.reopened": IconArrowRotateAnticlockwiseOutline18,
	"thread.ai_draft_ready": IconAtom2Outline18,
	"thread.ai_handoff": IconArrowsLeftRightTrailOutline18,
	"thread.snooze_ended": SnoozeEndedIcon,
	"thread.sla_at_risk": IconAlarmClockOutline18,
	"thread.sla_breached": IconAlarmClockOutline18,
	"thread.topic_added": IconTagOutline18,
	"thread.linked_issue_completed": IconCircleCheck3Outline18,
	"comment.mentioned": IconAtSignOutline18,
	"comment.created": IconMessagesOutline18,
	"issue.request_linked": IconMessagesOutline18,
	"issue.status_changed": IconCircleDashedOutline18,
	"project.request_linked": IconMessagesOutline18,
	"project.status_changed": IconCircleDashedOutline18,
	"docs.proposal_ready": IconBookOpen4Outline18,
	"docs.api_sync_held": IconBracketsCurlyOutline18,
	"docs.api_sync_failed": IconBracketsCurlyOutline18,
	"billing.payment_failed": IconCreditCardOutline18,
	"ai.cap_reached": IconGaugeOutline18,
	"slack.broadcast_disconnected": SlackIconGray as IconComponent,
	"slack.personal_dm_failed": SlackIconGray as IconComponent,
	"import.zendesk_finished": IconFileDownloadOutline18,
	"import.zendesk_failed": IconFileDownloadOutline18,
}

/** Shown when a notification has no person behind it; unknown types fall back to a bell. */
export function notificationTypeIcon(type: string): IconComponent {
	return ICONS[type] ?? IconBellOutline18
}

/** The thread's state beside the time, with the inbox list's glyphs; open threads show none. */
export function threadStateIcon(
	state: string | undefined,
): { Icon: IconComponent; className: string } | null {
	switch (state) {
		case "PROCESSED":
			return { Icon: IconCheckDoubleOutline18, className: "text-green-500" }
		case "COMPLETED":
			return {
				Icon: IconArrowDottedRotateAnticlockwiseOutline18,
				className: "text-green-600 dark:text-green-500",
			}
		case "SNOOZED":
			return { Icon: IconClockSnoozeOutline18, className: "text-tertiary" }
		default:
			return null
	}
}
